import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface PageHeaderProps {
  title: ReactNode;
  /** One sentence under the title; keep it for pages whose name does not explain them. */
  description?: ReactNode;
  /** The primary and secondary actions, right of the title. */
  actions?: ReactNode;
  /** Status badges next to the title. */
  badges?: ReactNode;
  /** Route tabs or a toolbar under the title row. */
  tabs?: ReactNode;
  /** `h1` on a page, `h2` on a sub-page that sits under a page title (settings). */
  as?: 'h1' | 'h2';
  className?: string;
}

/**
 * One header for every page: the title left, the actions on the same row,
 * wrapping under the title on narrow screens. Replaces the title row plus the
 * separate right-aligned action row that cost 56px on each list page.
 */
export function PageHeader({ title, description, actions, badges, tabs, as = 'h1', className }: PageHeaderProps) {
  const Heading = as;
  return (
    <div className={cn('mb-4 space-y-3', className)} data-slot="page-header">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <Heading
              className={cn(
                'min-w-0 break-words tracking-tight',
                as === 'h1' ? 'text-2xl font-semibold' : 'text-xl font-semibold',
              )}
            >
              {title}
            </Heading>
            {badges}
          </div>
          {description ? <p className="max-w-prose text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      {tabs}
    </div>
  );
}
